import axios from 'axios'
import type { components } from '@/types/generated'
import { bffBaseURL, normalizeError } from './apiClient'

// ============================================================================
// Candidates API — Phase 57
// ============================================================================

export async function uploadCandidate(file: File) {
  try {
    const formData = new FormData()
    formData.append('file', file)

    const response = await axios.post(
      `${bffBaseURL}/api/v1/candidates/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'CV upload failed')
      throw new Error(message)
    }
    throw error
  }
}

export async function uploadCandidatesBatch(files: File[]) {
  try {
    const formData = new FormData()
    files.forEach(file => {
      formData.append('files', file)
    })

    const response = await axios.post(
      `${bffBaseURL}/api/v1/candidates/batch`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Batch upload failed')
      throw new Error(message)
    }
    throw error
  }
}

export async function getCandidateStatus(
  candidateId: string
): Promise<components['schemas']['ProcessingStatusResponse']> {
  try {
    const response = await axios.get<components['schemas']['ProcessingStatusResponse']>(
      `${bffBaseURL}/api/v1/candidates/${candidateId}/status`
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to get candidate status')
      throw new Error(message)
    }
    throw error
  }
}

export async function getCandidateBatchStatus(jobId: string) {
  try {
    const response = await axios.get(
      `${bffBaseURL}/api/v1/candidates/batch/${jobId}/status`
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to get batch status')
      throw new Error(message)
    }
    throw error
  }
}

export async function getCandidates(params: Record<string, any> = {}) {
  try {
    const queryString = new URLSearchParams(params).toString()
    const response = await axios.get(
      `${bffBaseURL}/api/v1/candidates${queryString ? '?' + queryString : ''}`
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to load candidates')
      throw new Error(message)
    }
    throw error
  }
}

export async function getCandidate(candidateId: string) {
  try {
    const response = await axios.get(
      `${bffBaseURL}/api/v1/candidates/${candidateId}`
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to get candidate')
      throw new Error(message)
    }
    throw error
  }
}

export async function deleteCandidate(candidateId: string) {
  try {
    await axios.delete(
      `${bffBaseURL}/api/v1/candidates/${candidateId}`
    )
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to delete candidate')
      throw new Error(message)
    }
    throw error
  }
}

export async function updateCandidate(candidateId: string, updates: any) {
  try {
    const response = await axios.patch(
      `${bffBaseURL}/api/v1/candidates/${candidateId}`,
      updates
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to update candidate')
      throw new Error(message)
    }
    throw error
  }
}

// Phase 60 M8 — Candidate Notes

export async function getCandidateNotes(candidateId: string, params: Record<string, any> = {}) {
  try {
    const queryString = new URLSearchParams(params).toString()
    const response = await axios.get(
      `${bffBaseURL}/api/v1/candidates/${candidateId}/notes${queryString ? '?' + queryString : ''}`
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to load notes')
      throw new Error(message)
    }
    throw error
  }
}

export async function addCandidateNote(candidateId: string, content: string) {
  try {
    const response = await axios.post(
      `${bffBaseURL}/api/v1/candidates/${candidateId}/notes`,
      { content }
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to add note')
      throw new Error(message)
    }
    throw error
  }
}
